import { inject, Injectable, Injector } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { isUndefined } from 'lodash';
import { combineLatest, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AbstractAnnotationStore } from '../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { PointType } from '../../../team-apis/otterdam/answers/shape-typings';
import {
  ClientFeedback,
  ShapeFeedback,
} from '../../../team-apis/otterdam/feedback';
import { outputInfoStoreToken } from '../../h3ml/state/output-info';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager';
import { SelectionService } from '../../outputs/annotation/annotation-shared/utils/selection.service';
import { contentConnectorToken } from '../../outputs/annotation/utils/misc-utils';
import { AbstractFeedbackCrudService } from './crud/abstract-feedback-crud.service';
import { FeedbackListService } from './feedback-list.service';

@Injectable()
export abstract class AbstractFeedbackDialogService<
  T extends ShapeFeedback | ClientFeedback,
> extends InjectableSuperclass {
  protected fusionStore = inject(fusionStoreToken);
  protected taskWorkspaceStore = inject(TaskWorkspaceStore);
  protected store = inject(AbstractAnnotationStore);
  protected selectionService = inject(SelectionService);
  protected feedbackListService = inject(FeedbackListService);
  protected outputInfoStore = inject(outputInfoStoreToken);
  protected fusionManager = inject(FusionManager);

  protected abstract feedbackCrudService: AbstractFeedbackCrudService<T>;

  constructor(private injector: Injector) {
    super(injector);
    this.subscribeTo(this.store.ready$.pipe(take(1)), this.setUpSubscriptions);
  }

  setUpSubscriptions() {
    // Skip for raster
    if (!this.store.getActiveShapeLayerStore()) {
      return;
    }
    this.setUpSubscriptionToSelectedFeedback();
    this.setUpSubscriptionToSelectedFeedbackAndFrameNumber();
  }

  getSelectedFeedback() {
    const selectedId =
      this.taskWorkspaceStore.state().feedbackPopover.selectedId;
    if (isDefined(selectedId)) {
      return this.feedbackCrudService.getFeedback(selectedId);
    }
    return undefined;
  }

  clearFeedbackMarkers() {
    this.getContentConnector().setFeedbackMarkers([]);
  }

  protected setUpSubscriptionToSelectedFeedback() {
    this.subscribeTo(
      this.feedbackListService.getSelectedId$().pipe(filter(isDefined)),
      (selectedFeedbackId: string) => {
        this.selectAssociatedShapeAndScrubToStartFrame(selectedFeedbackId);
      },
    );
  }

  protected selectAssociatedShapeAndScrubToStartFrame(feedbackId: string) {
    const feedback = this.feedbackCrudService.getFeedback(feedbackId);

    assert(
      feedback,
      'selected feedback id should always have a corresponding feedback object',
    );

    this.changeOutput(feedback);
    this.modifyShapeSelection(feedback);
    this.scrubToFrame(feedback);
  }

  protected getContentConnector() {
    // to avoid circular injection issue
    return this.injector.get(contentConnectorToken);
  }

  protected scrubToFrame(feedback: T) {
    const frameNumber = this.getStartFrame(feedback);
    if (isDefined(frameNumber)) {
      this.fusionManager.scrubTo(frameNumber);
    }
  }

  protected reopenFeedbackListWithSelection(selectedFeedbackId: string) {
    this.feedbackListService.setSelection(selectedFeedbackId);
    this.feedbackListService.openFeedbackList('reopen');
  }

  protected clearFeedbackForAllOutputs() {
    this.fusionManager.minions.forEach((minion) =>
      minion.contentConnector.setFeedbackMarkers([]),
    );
  }

  protected isDialogOpen() {
    return this.taskWorkspaceStore.state().feedbackPopover.target === 'upsert';
  }

  private setUpSubscriptionToSelectedFeedbackAndFrameNumber() {
    this.subscribeTo(
      combineLatest([
        this.feedbackListService.getSelectedId$(),
        this.fusionManager.getFrameNumberForOutput$(
          this.outputInfoStore.state().output.name,
        ),
      ]),
      ([selectedFeedbackId, frameNumber]) => {
        if (isUndefined(selectedFeedbackId)) {
          if (!this.isDialogOpen()) {
            this.clearFeedbackMarkers();
          }
          return;
        }
        const selectedFeedback =
          this.feedbackCrudService.getFeedback(selectedFeedbackId);

        if (
          selectedFeedback &&
          this.feedbackCrudService.getFeedbackOutputId(selectedFeedback) ===
            this.outputInfoStore.state().output.id
        ) {
          this.displayAndInspectShapeMarkers(selectedFeedback, frameNumber);
        } else {
          this.clearFeedbackMarkers();
        }
      },
    );
  }

  private displayAndInspectShapeMarkers(feedback: T, frameNumber: number) {
    const coordinates =
      this.feedbackCrudService.getFeedbackCoordinates(feedback);
    const startFrame = this.getStartFrame(feedback);
    if ((startFrame === frameNumber || !isDefined(startFrame)) && coordinates) {
      this.feedbackCrudService.setFeedbackCoordinates(
        coordinates,
        this.isDialogOpen(),
        feedback,
      );
      void this.getContentConnector().inspectFeedbackMarker(coordinates);
    } else {
      this.clearFeedbackMarkers();
    }
  }

  abstract isEditable(): boolean;

  abstract closeDialog(): void;

  abstract closeView(): void;

  abstract openDetailView(point?: PointType | undefined): void;

  abstract openEditFeedbackDialog(): void;

  protected abstract changeOutput(feedback: T): void;

  protected abstract modifyShapeSelection(_feedback: T): void;

  protected abstract getStartFrame(feedback: T): number | undefined;
}
